// Ask the CRM: types, the call to the "ask" Edge Function, and the pure helpers the Ask page uses to edit a result
// without asking the model again (remove or change a filter, sort, pick columns, export).
// The model only ever writes a spec; crm_ask_query checks it and builds the SQL (docs/SECURITY.md).
import { env, functionUrl } from "./env";
import { httpFetch } from "./http";
import { summarise } from "./askSummary";
import type { ActionReply } from "./askActions";

export const ASK_SOURCES = ["firms", "people", "activity", "deals", "meetings", "events", "industry_events", "networks", "partners"] as const;
export type AskSource = (typeof ASK_SOURCES)[number];

export const ASK_OPS = [
  "eq", "neq", "contains", "not_contains", "starts_with", "gt", "gte", "lt", "lte", "in",
  "is_true", "is_false", "is_null", "not_null", "within_days", "older_than_days", "before", "after",
] as const;
export type AskOp = (typeof ASK_OPS)[number];
export const NO_VALUE_OPS: readonly string[] = ["is_true", "is_false", "is_null", "not_null"];

export interface AskFilter {
  field: string;
  op: AskOp | string;
  value?: unknown;
}
export interface AskSort {
  field: string;
  dir: "asc" | "desc";
}
export interface AskSpec {
  source: AskSource | string;
  filters: AskFilter[];
  sort?: AskSort[];
  columns?: string[];
  limit?: number;
}
export interface AskColumn {
  name: string;
  type: string;
}
export interface AskResult {
  spec: AskSpec;
  summary: string;
  columns: AskColumn[];
  rows: Record<string, unknown>[];
  total: number;
  truncated: boolean;
  /** When the question asked to do something: the proposed action and its preview. Never run until confirmed. */
  action?: ActionReply | null;
}

/** What crm_ask_query returns. */
export interface AskQueryResponse {
  total: number;
  rows: Record<string, unknown>[];
  columns: AskColumn[];
}

/** A query spec from another source (a supervisor table answer), or null when it is not one Ask can re-run. */
export function askSpecOf(x: unknown): AskSpec | null {
  if (!x || typeof x !== "object") return null;
  const o = x as Record<string, unknown>;
  if (!(ASK_SOURCES as readonly unknown[]).includes(o.source) || !Array.isArray(o.filters)) return null;
  const okFilter = (f: unknown) => Boolean(f) && typeof f === "object" && typeof (f as AskFilter).field === "string" && typeof (f as AskFilter).op === "string";
  if (!o.filters.every(okFilter)) return null;
  return x as AskSpec;
}

export function resultFrom(spec: AskSpec, r: AskQueryResponse): AskResult {
  const rows = r.rows ?? [];
  const total = Number(r.total ?? rows.length);
  return { spec, summary: summarise(spec, total), columns: r.columns ?? [], rows, total, truncated: total > rows.length };
}

// ------------------------------------------------------------------ the ask function
export class AskError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "AskError";
  }
}

export async function askQuestion(opts: {
  token: string;
  question: string;
  previousSpec?: AskSpec | null;
  url?: string;
  anonKey?: string;
  fetchImpl?: typeof fetch;
}): Promise<AskResult> {
  const f = opts.fetchImpl ?? httpFetch;
  let res: Response;
  try {
    res = await f(opts.url ?? functionUrl("ask"), {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${opts.token}`, apikey: opts.anonKey ?? env.supabaseAnonKey },
      body: JSON.stringify({ question: opts.question, ...(opts.previousSpec ? { previous_spec: opts.previousSpec } : {}) }),
    });
  } catch {
    throw new AskError("Can't reach the server. Check your connection and try again.", 0);
  }
  const body = (await res.json().catch(() => ({}))) as Partial<AskResult> & { error?: string };
  if (res.status === 404 && !body.error) throw new AskError("Asking in plain English isn't switched on yet. Try again later, or ask us for help.", 404);
  if (res.status === 401) throw new AskError("Your session has ended. Sign in again.", 401);
  if (!res.ok || !body.spec) throw new AskError(body.error ?? "Something went wrong. Try again in a moment.", res.status);
  const rows = Array.isArray(body.rows) ? body.rows : [];
  const total = Number(body.total ?? rows.length);
  return {
    spec: body.spec,
    summary: body.summary ?? summarise(body.spec, total),
    columns: Array.isArray(body.columns) ? body.columns : [],
    rows,
    total,
    truncated: Boolean(body.truncated ?? total > rows.length),
    action: body.action ?? null,
  };
}

// ------------------------------------------------------------------ editing a spec (no model)
export function withoutFilter(spec: AskSpec, index: number): AskSpec {
  return { ...spec, filters: spec.filters.filter((_, i) => i !== index) };
}

export function withFilterValue(spec: AskSpec, index: number, value: unknown): AskSpec {
  return { ...spec, filters: spec.filters.map((f, i) => (i === index ? { ...f, value } : f)) };
}

export function withFilter(spec: AskSpec, filter: AskFilter): AskSpec {
  return { ...spec, filters: [...spec.filters, filter] };
}

/** Clicking a header: first click sorts ascending (dates newest first), the next click flips it. */
export function toggleSort(spec: AskSpec, field: string, type = ""): AskSpec {
  const cur = spec.sort?.[0];
  const firstDir: "asc" | "desc" = /timestamp|date/.test(type) ? "desc" : "asc";
  const dir: "asc" | "desc" = cur?.field === field ? (cur.dir === "asc" ? "desc" : "asc") : firstDir;
  return { ...spec, sort: [{ field, dir }] };
}

export function withColumns(spec: AskSpec, columns: string[]): AskSpec {
  return { ...spec, columns };
}

/** Columns a person sees: id and firm_id are kept for links but never shown or exported. */
export const HIDDEN_COLUMNS = ["id", "firm_id"];
export function visibleColumns(columns: AskColumn[]): AskColumn[] {
  return columns.filter((c) => !HIDDEN_COLUMNS.includes(c.name));
}

/** Field names as headings: "last_contacted_at" -> "Last contacted"; "main_contact_email" -> "Main contact email". */
export function columnLabel(name: string): string {
  const special: Record<string, string> = {
    at: "Date", type: "Type", found_by: "Source", size_estimate: "Size estimate", starts_at: "Starts",
    // 3.14 connection strength (R-02) and 9.23 deal health (C-11)
    strength: "Strength score", strength_tier: "Strength", strongest_member: "Knows them best",
    health_reason: "Health reason", win_probability: "Win chance (%)", days_quiet: "Days quiet", last_contact_at: "Last contact",
  };
  if (special[name]) return special[name]!;
  const s = name.replace(/_at$/, "").replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export const OP_LABELS: Record<string, string> = {
  eq: "is",
  neq: "is not",
  contains: "contains",
  not_contains: "doesn't contain",
  starts_with: "starts with",
  gt: "more than",
  gte: "at least",
  lt: "less than",
  lte: "at most",
  in: "is one of",
  is_true: "yes",
  is_false: "no",
  is_null: "is empty",
  not_null: "is filled in",
  within_days: "in the last (days)",
  older_than_days: "more than (days) ago",
  before: "before",
  after: "after",
};

/** Operators that make sense for a column type, for the add-filter picker. */
export function opsForType(type: string): string[] {
  if (type === "boolean") return ["is_true", "is_false"];
  if (/timestamp|date/.test(type)) return ["within_days", "older_than_days", "before", "after", "is_null", "not_null"];
  if (/int|numeric|double|real|bigint/.test(type)) return ["eq", "gt", "gte", "lt", "lte", "is_null", "not_null"];
  return ["contains", "eq", "neq", "not_contains", "starts_with", "in", "is_null", "not_null"];
}

/** A filter as a short phrase for its chip: "City contains Glasgow", "Contacted: yes". */
export function filterLabel(f: AskFilter): { field: string; op: string; value: string | null } {
  const field = columnLabel(f.field);
  if (f.op === "is_true") return { field, op: "", value: "yes" };
  if (f.op === "is_false") return { field, op: "", value: "no" };
  if (f.op === "is_null" || f.op === "not_null") return { field, op: OP_LABELS[f.op] ?? f.op, value: null };
  if (f.op === "within_days") return { field, op: "in the last", value: `${String(f.value)} days` };
  if (f.op === "older_than_days") return { field, op: "more than", value: `${String(f.value)} days ago` };
  const kinds: Record<string, string> = { target: "prospect" };
  const value = Array.isArray(f.value) ? f.value.join(", ") : f.field === "type" ? (kinds[String(f.value)] ?? String(f.value ?? "")) : String(f.value ?? "");
  return { field, op: OP_LABELS[f.op] ?? f.op, value: value.replace(/_/g, " ") };
}

/** Turns what a person typed in a chip back into a value of the right shape. */
export function parseValue(op: string, text: string): unknown {
  const t = text.trim().replace(/\s*days?(\s+ago)?$/i, "");
  if (op === "in") return t.split(",").map((s) => s.trim()).filter(Boolean);
  if (op === "within_days" || op === "older_than_days") return Math.max(0, Math.floor(Number(t) || 0));
  if (["gt", "gte", "lt", "lte"].includes(op) && t !== "" && !Number.isNaN(Number(t))) return Number(t);
  return t;
}

// ------------------------------------------------------------------ export
function plain(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return typeof v === "object" ? JSON.stringify(v) : String(v);
}

/** Tab-separated, which pastes into a spreadsheet or a document as a table. */
export function toTsv(columns: AskColumn[], rows: Record<string, unknown>[]): string {
  const clean = (s: string) => s.replace(/[\t\r\n]+/g, " ");
  return [columns.map((c) => columnLabel(c.name)).join("\t"), ...rows.map((r) => columns.map((c) => clean(plain(r[c.name]))).join("\t"))].join("\n");
}

export function exportName(spec: AskSpec): string {
  const bits = [spec.source, ...spec.filters.filter((f) => typeof f.value === "string").map((f) => String(f.value))]
    .join("-")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
  return `${bits || "ask"}-${new Date().toISOString().slice(0, 10)}.csv`;
}

// ------------------------------------------------------------------ saved questions
// A question about firms or people is saved as a view (saved_views, act save_view: R-07), so it is on Companies or
// People and on every device. Questions over the other Ask lists (sends and replies, deals, meetings, events) have no
// list page, so they stay in this browser as before. The old browser-only firm and people questions are copied across
// once (copyOldQuestions).
export interface SavedQuestion {
  name: string;
  spec: AskSpec;
  saved_at: string;
}

const savedKey = (tenant: string) => `crm.ask.saved.${tenant}`;

/** Saved questions live in this browser's storage, per business. Storage can be blocked: then nothing is saved. */
export function loadSaved(tenant: string): SavedQuestion[] {
  try {
    const raw = window.localStorage.getItem(savedKey(tenant));
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list) ? list.filter((s): s is SavedQuestion => Boolean(s && typeof s.name === "string" && s.spec && typeof s.spec.source === "string")) : [];
  } catch {
    return [];
  }
}

/** Returns the new list, and whether it was stored. */
export function storeSaved(tenant: string, list: SavedQuestion[]): boolean {
  try {
    window.localStorage.setItem(savedKey(tenant), JSON.stringify(list.slice(0, 30)));
    return true;
  } catch {
    return false;
  }
}

/** A question that can be a view on Companies or People. */
export const isViewSource = (source: unknown): source is "firms" | "people" => source === "firms" || source === "people";

const copiedKey = (tenant: string) => `crm.ask.copied.${tenant}`;

/**
 * Copies this browser's saved firm and people questions into saved views, once per business. Storage that is empty,
 * blocked or broken is harmless: nothing is copied and nothing throws. A question whose name is already a view counts
 * as copied. Returns how many were copied; the copied ones leave this browser's list.
 */
export async function copyOldQuestions(tenant: string, save: (q: SavedQuestion) => Promise<unknown>): Promise<number> {
  try {
    if (window.localStorage.getItem(copiedKey(tenant))) return 0;
  } catch {
    return 0;
  }
  const list = loadSaved(tenant);
  const keep: SavedQuestion[] = [];
  let copied = 0;
  let failed = false;
  for (const q of list) {
    if (!isViewSource(q.spec.source)) {
      keep.push(q);
      continue;
    }
    try {
      await save(q);
      copied++;
    } catch (e) {
      if (/already have a view/i.test((e as Error).message)) continue;
      failed = true; // try again next time
      keep.push(q);
    }
  }
  storeSaved(tenant, keep);
  if (!failed) {
    try {
      window.localStorage.setItem(copiedKey(tenant), new Date().toISOString());
    } catch {
      // blocked storage: nothing to remember it in
    }
  }
  return copied;
}

// ------------------------------------------------------------------ example questions
export const SEARCH_EXAMPLES = [
  "Contacted prospects in Glasgow",
  "Water hygiene firms we haven't contacted",
  "Replies this week",
  "Firms with a meeting booked",
  "Deals at NDA",
  "Firms we're strong with",
  "Deals at risk",
];
export const GENERIC_EXAMPLES = ["Prospects we haven't contacted", "Replies this week", "Firms with a meeting booked", "Open deals", "People who replied",
  "People we haven't spoken to in 90 days", "Deals at risk"];

/** Examples that fit the business: an acquisition search gets the compliance-sector ones. */
export function examplesFor(config: unknown, name = ""): string[] {
  const text = `${name} ${JSON.stringify(config ?? {})}`.toLowerCase();
  return /acqui|search fund|buy-and-build|compliance|inspection|testing|heimdall/.test(text) ? SEARCH_EXAMPLES : GENERIC_EXAMPLES;
}
